import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { StatCard, card, Badge } from "./ui";
import { Inbox, Truck, CheckCircle2, UtensilsCrossed, Package, Building2, Users, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from "recharts";

const statusTone = { NEW: "gold", CONTACTED: "blue", "FOLLOW-UP": "blue", QUOTED: "blue", NEGOTIATION: "gold", CONFIRMED: "green", COMPLETED: "green", CANCELLED: "red" };

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-ivory/40">Loading dashboard…</p>;
  const s = d.stats;
  const statusData = Object.entries(d.status_counts).map(([name, value]) => ({ name, value }));
  const catData = Object.entries(d.category_counts).map(([name, value]) => ({ name: name.replace(" ENQUIRY", ""), value }));
  const COLORS = ["#D4AF37", "#801222", "#6B111D", "#C5A059", "#E6C670", "#4A0E17", "#a56a2a", "#5c3a1a"];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <p className="text-gold/70 text-xs tracking-[0.25em] uppercase">Executive Dashboard</p>
        <h1 className="font-serif text-4xl text-ivory mt-1">Welcome back.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="New Events" value={s.new_event} icon={Inbox} />
        <StatCard label="New Bulk" value={s.new_bulk} icon={Truck} />
        <StatCard label="Confirmed" value={s.confirmed} icon={CheckCircle2} />
        <StatCard label="Menu Items" value={s.menu_items} icon={UtensilsCrossed} />
        <StatCard label="Packages" value={s.packages} icon={Package} />
        <StatCard label="Total Enquiries" value={s.total_enquiries} icon={Inbox} />
        <StatCard label="Contracts" value={s.contracts} icon={Truck} />
        <StatCard label="Branches" value={s.branches} icon={Building2} />
        <StatCard label="Brands" value={s.brands} icon={Building2} />
        <StatCard label="Staff" value={s.staff} icon={Users} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`${card} p-5 lg:col-span-2`}>
          <h3 className="font-serif text-xl text-gold mb-4">Enquiry Pipeline</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fill: "#FDFBF7", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis tick={{ fill: "#FDFBF7", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1C1210", border: "1px solid #D4AF37", borderRadius: 8, color: "#FDFBF7" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4">By Category</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {catData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#1C1210", border: "1px solid #D4AF37", borderRadius: 8, color: "#FDFBF7" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`${card} p-5 lg:col-span-2`}>
          <h3 className="font-serif text-xl text-gold mb-4">Recent Enquiries</h3>
          <div className="space-y-2">
            {d.recent_enquiries.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                <div><p className="text-ivory text-sm">{e.name}</p><p className="text-ivory/40 text-xs">{e.event_type} · {e.guests} guests · {e.location}</p></div>
                <Badge tone={statusTone[e.status] || "gray"}>{e.status}</Badge>
              </div>
            ))}
          </div>
        </div>
        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Upcoming Events</h3>
          {d.upcoming_events.length ? d.upcoming_events.map((e) => (
            <div key={e.id} className="py-2.5 border-b border-white/5 last:border-0">
              <p className="text-ivory text-sm">{e.event_type}</p>
              <p className="text-ivory/40 text-xs">{e.event_date} · {e.name}</p>
            </div>
          )) : <p className="text-ivory/40 text-sm">No confirmed events yet.</p>}
        </div>
      </div>

      <div className={`${card} p-5`}>
        <h3 className="font-serif text-xl text-gold mb-4">Recent Activity</h3>
        <div className="space-y-1.5">
          {d.recent_activity.map((a) => (
            <div key={a.id} className="flex items-center gap-3 text-sm py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
              <span className="text-ivory/80">{a.action}</span>
              <span className="text-ivory/40 truncate">{a.detail}</span>
              <span className="text-ivory/30 text-xs ml-auto shrink-0">{a.actor_name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
